import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Row, Col } from 'react-bootstrap';
import { usePerfil } from '../../../hooks/perfil';

import { useToast } from '../../../hooks/toast';

import Loading from '../../../components/Loading';
import InputFake from '../../../components/InputFake';
import SelectSync, { IOption } from '../../../components/SelectSync';

import getValidationErrors from '../../../utils/getValidationErrors';

import api from '../../../services/api';

import {
  IDadosLista,
  IServicoNFDTO,
  formataServicoNF,
  ICreateServicoNFDTO,
} from '../ListaServicos';
import getResponseErrors from '../../../utils/getResponseErrors';
import Input from '../../../components/Input';
import { IOptions } from '../../../utils/options';

type Props = {
  serCodigo?: number;
  labels?: IDadosLista;
  addRegistro(
    dadosServicoCadastro: ICreateServicoNFDTO,
    dadosLista: IDadosLista,
    serCodigo?: number,
  ): void;
};

const ServicoCadastroPagina: React.FC<Props> = ({
  serCodigo,
  labels,
  addRegistro,
}) => {
  const { optionsPerfil, buscaPerfil } = usePerfil();

  const { addToast } = useToast();

  const formRefServicoNF = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [perfisComCadastro, setPerfisComCadastro] = useState<number[]>();
  const [optionsPerfis, setOptionsPerfis] = useState<IOption[]>();
  const [servicoNF, setServicoNF] = useState<IServicoNFDTO | undefined>(
    undefined,
  );

  useEffect(() => {
    async function loadPerfis(): Promise<void> {
      await buscaPerfil();
    }
    if (!optionsPerfil || Object.keys(optionsPerfil).length === 0) loadPerfis();
  }, [buscaPerfil, optionsPerfil]);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IServicoNFDTO>(`serviconf/${serCodigo}`);

        setServicoNF(formataServicoNF(response.data));
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar dados do serviço',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }

    async function loadPerfis(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<number[]>(
          'serviconf/buscaPerfisComServico',
        );
        setPerfisComCadastro(response.data);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar os perfis cadastrados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (serCodigo !== undefined) loadDados();
    if (serCodigo === undefined) loadPerfis();
  }, [serCodigo, addToast]);

  useEffect(() => {
    let opcoesFiltradas: IOptions[] = [];
    if (perfisComCadastro)
      opcoesFiltradas = optionsPerfil.optionPerfis.filter(
        opcao =>
          !opcao.isDisabled && !perfisComCadastro.includes(Number(opcao.value)),
      );
    if (!perfisComCadastro)
      opcoesFiltradas = optionsPerfil.optionPerfis.filter(
        opcao => !opcao.isDisabled,
      );

    setOptionsPerfis(opcoesFiltradas);
  }, [optionsPerfil, perfisComCadastro]);

  const ehNovo = useMemo(() => {
    return serCodigo === undefined;
  }, [serCodigo]);

  const handleSubmitServico = useCallback(
    async (dados: IServicoNFDTO) => {
      try {
        setLoading(true);
        formRefServicoNF.current?.setErrors({});
        const schema = Yup.object().shape({
          ...(ehNovo && { SerPerCodigo: Yup.number().required() }),

          SerCodigoServico: Yup.string().required().max(5),
          SerDescricao: Yup.string().required().max(100),
          SerCNAE: Yup.string().max(7),
          SerAliquota: Yup.string().required().max(6),
          SerCodigoTributacao: Yup.string().max(15),
          SerTipoTributacao: Yup.number()
            .nullable()
            .transform((value, originalValue) => {
              if (originalValue === '') return null;
              return value;
            }),
          SerCodigoNBS: Yup.string().max(10),
          SerIndicOp: Yup.string().max(6),
          SerIbsClassTrib: Yup.string().max(6),
          SerIbsSituTrib: Yup.string().max(3),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dadosEnvio: ICreateServicoNFDTO = {
          ...dados,
        };

        if (ehNovo) {
          await addRegistro(dadosEnvio, {
            perfil: dados.SerPerCodigo.toString(),
          });
        } else {
          await addRegistro(
            dadosEnvio,
            {
              perfil: labels?.perfil,
            },
            serCodigo,
          );
        }
      } catch (error) {
        console.log(error);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefServicoNF.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Erro ao salvar Servico',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, addRegistro, ehNovo, serCodigo, labels],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <div className="container">
      {loading && <Loading />}

      <Row noGutters>
        <Col md={12}>
          {(ehNovo || servicoNF) && (
            <>
              <Form
                ref={formRefServicoNF}
                initialData={{
                  ...servicoNF,
                }}
                onSubmit={handleSubmitServico}
              >
                <Row>
                  <Col md={12} className="form-group pr-2">
                    {ehNovo && optionsPerfis ? (
                      <SelectSync
                        name="SerPerCodigo"
                        label="Perfil"
                        placeholder="Selecione"
                        handleSelectChange={handleOnChangeVazio}
                        options={optionsPerfis}
                        isDisabled={!ehNovo}
                      />
                    ) : (
                      <InputFake
                        label="Perfil"
                        value={servicoNF?.perfil.PerNome}
                        disabled
                      />
                    )}
                  </Col>
                </Row>

                <Row>
                  <Col md={12} className="form-group pr-2">
                    <Input
                      name="SerDescricao"
                      label="Descrição Serviço"
                      textUppercase={false}
                      maxLength={100}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={4} className="form-group pr-2">
                    <Input name="SerAliquota" label="Aliquota" maxLength={6} />
                  </Col>
                  <Col md={4} className="pr-md-2">
                    <Input
                      name="SerCodigoServico"
                      label="Cod. Serviço"
                      maxLength={5}
                    />
                  </Col>
                  <Col md={4} className="form-group pr-2">
                    <Input name="SerCNAE" label="CNAE" maxLength={7} />
                  </Col>
                </Row>

                <Row>
                  <Col md={4} className="pr-md-2">
                    <Input
                      name="SerTipoTributacao"
                      label="Tipo Tributação"
                      maxLength={10}
                      max={10}
                      type="number"
                    />
                  </Col>
                  <Col md={4} className="pr-md-2">
                    <Input
                      name="SerCodigoTributacao"
                      label="Cod. Tributação"
                      maxLength={15}
                    />
                  </Col>
                  <Col md={4} className="form-group pr-2">
                    <Input
                      name="SerIndicOp"
                      label="Indicador de Operação"
                      maxLength={6}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={4} className="form-group pr-2">
                    <Input
                      name="SerCodigoNBS"
                      label="Cod. NBS"
                      maxLength={10}
                    />
                  </Col>
                  <Col md={4} className="pr-md-2">
                    <Input
                      name="SerIbsClassTrib"
                      label="Ibs Classificação tributária"
                      maxLength={6}
                    />
                  </Col>
                  <Col md={4} className="form-group pr-2">
                    <Input
                      name="SerIbsSituTrib"
                      label="Ibs Situação tributária"
                      maxLength={3}
                    />
                  </Col>
                </Row>

                {!ehNovo && servicoNF && (
                  <Row>
                    <Col md={5} className="form-group pr-2">
                      <InputFake
                        label="Ultima Atualização"
                        value={servicoNF.updated_atFormatado}
                      />
                    </Col>

                    <Col md={7} className="pr-md-2">
                      <InputFake
                        label="Usuário"
                        value={servicoNF.usuario.UsuNome}
                      />
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col md={6}>
                    <div className="text-right align-items-center p-1">
                      <>
                        <Button type="submit" variant="success">
                          Salvar dados
                        </Button>
                      </>
                    </div>
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ServicoCadastroPagina;
